import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Card,
  Col,
  Modal,
  Form,
  Button,
  Offcanvas,
  Badge,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import Breadcrumb from "Common/BreadCrumb";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import ModalEdit from "./ModalEdit";
import {
  MatieresToAdd,
  useAddMatiereMutation,
  useDeleteMatiereMutation,
  useFetchMatieresQuery,
} from "features/matieres/matiereSlice";
import { useGetNiveauxQuery } from "features/niveaux/niveauxSlice";

const Matieres = () => {
  const { data = [] } = useFetchMatieresQuery();

  const { data: AllNiveaux = [] } = useGetNiveauxQuery();

  const [showMatiere, setShowMatiere] = useState<boolean>(false);

  const [deleteMatiere] = useDeleteMatiereMutation();

  const notifySuccess = () => {
    Swal.fire({
      position: "center",
      icon: "success",
      title: "La matière a été créée avec succès",
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const notifyError = (err: any) => {
    Swal.fire({
      position: "center",
      icon: "error",
      title: `Une erreur est survenue: ${err}`,
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-danger",
    },
    buttonsStyling: false,
  });

  const AlertDelete = async (_id: any) => {
    swalWithBootstrapButtons
      .fire({
        title: "Etes-vous sûr?",
        text: "Vous ne pouvez pas revenir en arrière?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, supprime-le !",
        cancelButtonText: "Non, annuler !",
        reverseButtons: true,
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteMatiere(_id);
          swalWithBootstrapButtons.fire(
            "Supprimé !",
            "La matière est supprimée.",
            "success"
          );
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          swalWithBootstrapButtons.fire(
            "Annulé",
            "La matière est en sécurité :)",
            "info"
          );
        }
      });
  };

  const [modal_AddMatiere, setmodal_AddMatiere] = useState<boolean>(false);
  function tog_AddMatiere() {
    setmodal_AddMatiere(!modal_AddMatiere);
  }
  const [modal_UpdateMatiere, setmodal_UpdateMatiere] =
    useState<boolean>(false);
  function tog_UpdateMatiere() {
    setmodal_UpdateMatiere(!modal_UpdateMatiere);
  }

  const [selectedNiveau, setSelectedNiveau] = useState<string>("");

  const handleSelectNiveau = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setSelectedNiveau(value);
  };

  const [createMatiere] = useAddMatiereMutation();

  const initialMatiere = {
    matieres: [{ nom_matiere: "" }],
    niveau: "",
  };

  const [matiere, setMatiere] = useState(initialMatiere);

  const handleAddMatiere = () => {
    setMatiere((prevState) => ({
      ...prevState,
      matieres: [...prevState.matieres, { nom_matiere: "" }],
    }));
  };

  const handleRemoveMatiere = (index: number) => {
    setMatiere((prevState) => ({
      ...prevState,
      matieres: prevState.matieres.filter((_, i) => i !== index),
    }));
  };

  const handleMatiereChange = (index: number, value: any) => {
    const updatedMatieres = matiere.matieres.map((item, i) =>
      i === index ? { ...item, nom_matiere: value } : item
    );
    setMatiere((prevState) => ({
      ...prevState,
      matieres: updatedMatieres,
    }));
  };

  const onSubmitMatiere = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      matiere["niveau"] = selectedNiveau;
      createMatiere(matiere)
        .then(() => notifySuccess())
        .then(() => setMatiere(initialMatiere));
    } catch (error) {
      notifyError(error);
    }
  };

  // Filters
  const [filterNiveau, setFilterNiveau] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Stats
  const stats = useMemo(() => {
    const totalMatieres = data.reduce(
      (acc: number, item: any) => acc + (item.matieres?.length || 0),
      0
    );
    const niveaux = new Set(data.map((m: any) => m?.niveau?.nom_niveau)).size;
    return { totalGroups: data.length, totalMatieres, niveaux };
  }, [data]);

  const getFilteredMatieres = () => {
    let filteredMatieres = [...data];

    if (filterNiveau) {
      filteredMatieres = filteredMatieres.filter(
        (m: any) => m?.niveau?.nom_niveau === filterNiveau
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredMatieres = filteredMatieres.filter(
        (matiere: any) =>
          matiere.matieres.some((m: MatieresToAdd) =>
            m.nom_matiere.toLowerCase().includes(term)
          ) ||
          matiere.niveau.nom_niveau.toLowerCase().includes(term)
      );
    }

    return filteredMatieres;
  };

  // Unique niveaux for filter
  const uniqueNiveaux = useMemo(() => {
    const set = new Set<string>();
    data.forEach((m: any) => {
      if (m?.niveau?.nom_niveau) set.add(m.niveau.nom_niveau);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [data]);

  const columns = [
    {
      name: <span className="fw-semibold fs-13">Niveau</span>,
      selector: (row: any) => row.niveau.nom_niveau,
      cell: (row: any) => (
        <Badge bg="info-subtle" text={"info" as any} className="fs-12 px-2 py-1">
          <i className="ri-graduation-cap-line me-1" />
          {row.niveau.nom_niveau}
        </Badge>
      ),
      sortable: true,
    },
    {
      name: <span className="fw-semibold fs-13">Matières</span>,
      selector: (row: any) =>
        row.matieres.map((matiere: any) => matiere.nom_matiere).join(", "),
      cell: (row: any) => (
        <div className="d-flex flex-wrap gap-1">
          {row.matieres.map((m: any, i: number) => (
            <Badge key={i} bg="primary-subtle" text={"primary" as any} className="fs-12 px-2 py-1">
              <i className="ri-book-2-line me-1" />
              {m.nom_matiere}
            </Badge>
          ))}
        </div>
      ),
      sortable: true,
      grow: 3,
    },
    {
      name: <span className="fw-semibold fs-13">Actions</span>,
      cell: (row: any) => (
        <div className="d-flex gap-1">
          <Link
            to="#"
            state={row}
            className="btn btn-soft-info btn-sm btn-icon"
            onClick={() => setShowMatiere(true)}
            title="Voir détails"
          >
            <i className="ri-eye-line" />
          </Link>
          <Link
            to="#"
            state={row}
            className="btn btn-soft-success btn-sm btn-icon"
            onClick={() => tog_UpdateMatiere()}
            title="Modifier"
          >
            <i className="ri-edit-2-line" />
          </Link>
          <button
            className="btn btn-soft-danger btn-sm btn-icon"
            onClick={() => AlertDelete(row._id)}
            title="Supprimer"
          >
            <i className="ri-delete-bin-2-line" />
          </button>
        </div>
      ),
    },
  ];

  const matiereLocation = useLocation();

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f3f6f9",
        fontWeight: "600",
        fontSize: "13px",
        color: "#495057",
        borderBottom: "2px solid #e9ebec",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        "&:hover": {
          backgroundColor: "#f3f6f9",
        },
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e9ebec",
      },
    },
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Matières" pageTitle="Tableau de bord" />

          {/* KPI Cards */}
          <Row className="mb-3">
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm cursor-pointer" onClick={() => setFilterNiveau("")}>
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                        <i className="ri-book-2-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Total Matières</p>
                      <h4 className="mb-0">{stats.totalMatieres}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                        <i className="ri-stack-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Groupes</p>
                      <h4 className="mb-0 text-success">{stats.totalGroups}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={3}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-3">
                        <i className="ri-graduation-cap-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Niveaux</p>
                      <h4 className="mb-0 text-warning">{stats.niveaux}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Data Table */}
          <Col lg={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="border-bottom-dashed bg-white">
                <Row className="g-3 align-items-center">
                  <Col lg={4} md={6}>
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Rechercher par matière, niveau..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </Col>
                  <Col lg={3} md={3}>
                    <select
                      className="form-select"
                      value={filterNiveau}
                      onChange={(e) => setFilterNiveau(e.target.value)}
                    >
                      <option value="">Tous les niveaux</option>
                      {uniqueNiveaux.map((n) => (
                        <option value={n} key={n}>{n}</option>
                      ))}
                    </select>
                  </Col>
                  <Col lg={5} md={3} className="d-flex justify-content-end gap-2">
                    {(filterNiveau || searchTerm) && (
                      <Button
                        variant="soft-secondary"
                        size="sm"
                        onClick={() => { setFilterNiveau(""); setSearchTerm(""); }}
                      >
                        <i className="ri-filter-off-line me-1" />
                        Réinitialiser
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => tog_AddMatiere()}
                      className="d-flex align-items-center gap-1"
                    >
                      <i className="ri-add-line fs-16" />
                      Nouvelle matière
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable
                  columns={columns}
                  data={getFilteredMatieres()}
                  pagination
                  paginationPerPage={15}
                  paginationRowsPerPageOptions={[10, 15, 25, 50]}
                  customStyles={customStyles}
                  noDataComponent={
                    <div className="text-center py-5">
                      <i className="ri-book-2-line fs-1 text-muted d-block mb-2" />
                      <p className="text-muted mb-0">Aucune matière trouvée</p>
                    </div>
                  }
                  paginationComponentOptions={{
                    rowsPerPageText: "Lignes par page",
                    rangeSeparatorText: "sur",
                  }}
                  highlightOnHover
                  responsive
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Add Modal */}
          <Modal
            className="fade"
            show={modal_AddMatiere}
            onHide={() => tog_AddMatiere()}
            centered
          >
            <Modal.Header closeButton className="bg-primary-subtle">
              <h5 className="modal-title">
                <i className="ri-add-circle-line me-2" />
                Nouvelle matière
              </h5>
            </Modal.Header>
            <Modal.Body>
              <Form className="create-form" onSubmit={onSubmitMatiere}>
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Label htmlFor="niveau">Niveau</Form.Label>
                    <Select
                      options={[...AllNiveaux].sort((a: any, b: any) => (a.nom_niveau || "").localeCompare(b.nom_niveau || "", "fr")).map((niveau: any) => ({
                        value: niveau._id,
                        label: niveau.nom_niveau,
                      }))}
                      onChange={(opt: any) => { const e = { target: { value: opt?.value || "" } } as any; handleSelectNiveau(e); }}
                      placeholder="Rechercher un niveau..."
                      isClearable
                      isSearchable
                      noOptionsMessage={() => "Aucun niveau trouvé"}
                    />
                  </Col>
                </Row>
                {matiere.matieres.map((item, index) => (
                  <Row className="mt-3" key={index}>
                    <Col lg={8}>
                      <Form.Label htmlFor={`nom_matiere_${index}`}>
                        Matière {index + 1}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        id={`nom_matiere_${index}`}
                        name={`nom_matiere_${index}`}
                        placeholder="Nom de la matière"
                        className="w-100"
                        value={item.nom_matiere}
                        onChange={(e) =>
                          handleMatiereChange(index, e.target.value)
                        }
                      />
                    </Col>
                    <Col lg={2} className="d-flex align-items-end">
                      {index === matiere.matieres.length - 1 && (
                        <button
                          type="button"
                          className="btn btn-soft-info btn-icon"
                          onClick={handleAddMatiere}
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      )}
                    </Col>
                    <Col lg={2} className="d-flex align-items-end">
                      <button
                        type="button"
                        className="btn btn-danger btn-icon"
                        onClick={() => handleRemoveMatiere(index)}
                      >
                        <i className="ri-delete-bin-5-line"></i>
                      </button>
                    </Col>
                  </Row>
                ))}
                <div className="hstack gap-2 justify-content-end mt-4">
                  <Button
                    variant="light"
                    onClick={() => {
                      tog_AddMatiere();
                      setMatiere(initialMatiere);
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => tog_AddMatiere()}
                    type="submit"
                    variant="primary"
                  >
                    <i className="ri-check-line me-1" />
                    Enregistrer
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          {/* Update Modal */}
          <Modal
            className="fade"
            show={modal_UpdateMatiere}
            onHide={() => tog_UpdateMatiere()}
            centered
          >
            <Modal.Header closeButton className="bg-success-subtle">
              <h5 className="modal-title">
                <i className="ri-edit-2-line me-2" />
                Modifier matière
              </h5>
            </Modal.Header>
            <Modal.Body>
              <ModalEdit
                modal_UpdateMatiere={modal_UpdateMatiere}
                setmodal_UpdateMatiere={setmodal_UpdateMatiere}
              />
            </Modal.Body>
          </Modal>

          {/* Detail Offcanvas */}
          <Offcanvas
            show={showMatiere}
            onHide={() => setShowMatiere(!showMatiere)}
            placement="end"
            style={{ width: "420px" }}
          >
            <Offcanvas.Header closeButton className="border-bottom bg-light">
              <Offcanvas.Title className="d-flex align-items-center gap-2">
                <i className="ri-book-2-line text-primary fs-20" />
                Détails Matière
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <div className="mb-4">
                <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                  Niveau
                </label>
                <Badge bg="info-subtle" text={"info" as any} className="fs-13 px-3 py-2">
                  <i className="ri-graduation-cap-line me-1" />
                  {matiereLocation?.state?.niveau?.nom_niveau!}
                </Badge>
              </div>
              <div className="mb-4">
                <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                  Matières
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {matiereLocation?.state?.matieres?.map(
                    (matiere: any, index: number) => (
                      <Badge key={index} bg="primary-subtle" text={"primary" as any} className="fs-13 px-3 py-2">
                        <i className="ri-book-2-line me-1" />
                        {matiere.nom_matiere}
                      </Badge>
                    )
                  )}
                </div>
              </div>
            </Offcanvas.Body>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};
export default Matieres;
